import { useConvexAuth } from "convex/react";
import { useAction, useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { SEO } from "../components/SEO";

interface PricingTier {
  name: string;
  price: string;
  period: string;
  description: string;
  features: string[];
  cta: string;
  highlighted?: boolean;
  priceId?: string;
}

// Stripe Price IDs from environment variables
const PRICE_ID_PRO = import.meta.env.VITE_STRIPE_PRICE_ID_PRO || "";
const PRICE_ID_BUSINESS = import.meta.env.VITE_STRIPE_PRICE_ID_BUSINESS || "";

const tiers: PricingTier[] = [
  {
    name: "Starter",
    price: "Free",
    period: "",
    description: "For prototypes and personal projects",
    features: [
      "100 requests/day",
      "1 API key",
      "Full catalog search",
      "Streaming URLs",
      "Full metadata + lyrics",
      "Attribution required",
    ],
    cta: "Get started",
  },
  {
    name: "Pro",
    price: "$29",
    period: "/month",
    description: "For apps and integrations",
    features: [
      "1,000 requests/day",
      "5 API keys",
      "Full catalog search",
      "Streaming URLs",
      "No attribution required",
      "Full metadata + lyrics",
    ],
    cta: "Subscribe",
    highlighted: true,
    priceId: PRICE_ID_PRO,
  },
  {
    name: "Business",
    price: "$99",
    period: "/month",
    description: "For teams and production apps",
    features: [
      "10,000 requests/day",
      "20 API keys",
      "Full catalog search",
      "Streaming URLs",
      "No attribution required",
      "Full metadata + lyrics",
      "Priority support",
    ],
    cta: "Subscribe",
    priceId: PRICE_ID_BUSINESS,
  },
  {
    name: "Enterprise",
    price: "Custom",
    period: "",
    description: "For large-scale deployments",
    features: [
      "Unlimited requests",
      "Unlimited API keys",
      "Custom catalog curation",
      "SLA guarantee",
      "Dedicated support",
      "White-label options",
    ],
    cta: "Contact us",
  },
];

export function Pricing() {
  const { isAuthenticated } = useConvexAuth();
  const subscription = isAuthenticated
    ? useQuery(api.mcpPortal.getMySubscription)
    : null;
  const createCheckout = isAuthenticated
    ? useAction(api.mcpStripe.createCheckoutSession)
    : null;

  const handleSubscribe = async (priceId: string) => {
    if (!isAuthenticated) {
      window.location.href = "/login";
      return;
    }
    if (!createCheckout) return;

    try {
      const result = await createCheckout({
        priceId,
        successUrl: `${window.location.origin}/dashboard?success=true`,
        cancelUrl: `${window.location.origin}/pricing`,
      });
      window.location.href = result.url;
    } catch (err: any) {
      alert(err.message);
    }
  };

  const currentTier = subscription?.tier ?? "free";

  return (
    <>
      <SEO
        title="Pricing | StarSinger MCP"
        description="StarSinger MCP pricing: API plans for catalog requests (free 100/day, Pro $29/month). Songs and music videos are billed to each user's StarSinger account — one free song a day, then $1.99."
        pathname="/pricing"
      />
      <div className="max-w-6xl mx-auto px-6 py-16">
        <div className="text-center mb-14">
          <h1 className="text-4xl font-bold mb-4">Simple, transparent pricing</h1>
        <p className="text-text-muted text-lg">
          Start free. Scale when you need to.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {tiers.map((tier) => {
          const isCurrent =
            (tier.name === "Starter" && currentTier === "free") ||
            (tier.name === "Pro" && currentTier === "pro") ||
            (tier.name === "Business" && currentTier === "business");

          return (
            <div
              key={tier.name}
              className={`rounded-xl p-6 flex flex-col ${
                tier.highlighted
                  ? "border-2 border-brand bg-surface-light ring-1 ring-brand/20"
                  : "border border-border bg-surface-light"
              }`}
            >
              <div className="mb-6">
                <h3 className="font-semibold text-lg mb-1">{tier.name}</h3>
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-bold">{tier.price}</span>
                  {tier.period && (
                    <span className="text-text-muted text-sm">{tier.period}</span>
                  )}
                </div>
                <p className="text-sm text-text-muted mt-2">{tier.description}</p>
              </div>

              <ul className="space-y-3 mb-8 flex-1">
                {tier.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2 text-sm">
                    <Check className="w-4 h-4 text-brand shrink-0 mt-0.5" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              {isCurrent ? (
                <div className="text-center py-2.5 rounded-lg border border-border text-sm text-text-muted">
                  Current plan
                </div>
              ) : tier.name === "Enterprise" ? (
                <a
                  href="mailto:team@starsinger.ai?subject=StarSinger MCP Enterprise"
                  className="text-center py-2.5 rounded-lg border border-border hover:border-brand/50 text-sm font-medium transition-colors"
                >
                  {tier.cta}
                </a>
              ) : tier.priceId ? (
                <button
                  onClick={() => handleSubscribe(tier.priceId!)}
                  className={`py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    tier.highlighted
                      ? "bg-brand hover:bg-brand-dark text-white"
                      : "border border-border hover:border-brand/50"
                  }`}
                >
                  {tier.cta}
                </button>
              ) : (
                <Link
                  to="/login"
                  className="text-center py-2.5 rounded-lg border border-border hover:border-brand/50 text-sm font-medium transition-colors"
                >
                  {tier.cta}
                </Link>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-12 border border-border rounded-xl p-6 bg-surface-light text-sm text-text-muted leading-relaxed">
        <h2 className="text-base font-semibold text-text mb-2">Making songs and music videos</h2>
        <p>
          API plans cover catalog requests (search, tracks, artists, playlists). Songs and music videos made with{" "}
          <code className="text-xs bg-surface px-1 py-0.5 rounded">make_song</code> and{" "}
          <code className="text-xs bg-surface px-1 py-0.5 rounded">make_music_video</code> are billed to each user&apos;s own
          StarSinger account, whichever plan you are on: one free song a day (published to the public feed, with the
          user&apos;s OK), then $1.99 per song, and $19.99 for a music video starring the user. US prices; checkout shows
          the local price. No subscription, and nothing is charged inside the chat —{" "}
          <Link to="/docs#songs" className="text-brand hover:underline">how it works</Link>.
        </p>
      </div>
      </div>
    </>
  );
}
